import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

/**
 * A product's early access: `accessible_from`, set by hand, opens the product
 * before its market premiere (`published_at`). The backend refuses one only
 * when there is no premiere at all — a date on or after the premiere is
 * stored but opens nothing, so the UI has to say so. Compared at day
 * granularity: the operator picks a calendar day.
 */
export type EarlyAccessState = 'scheduled' | 'active' | 'ended' | 'ineffective';

interface EarlyAccessDates {
  published_at?: string | null;
  accessible_from?: string | null;
}

/** `null` when no early access is set. */
export const earlyAccessState = (
  { published_at, accessible_from }: EarlyAccessDates,
  now: Dayjs = dayjs()
): EarlyAccessState | null => {
  if (!accessible_from) return null;
  // The import may since have dropped the premiere; nothing opens without it.
  if (!published_at || !dayjs(accessible_from).isBefore(published_at, 'day'))
    return 'ineffective';
  if (!now.isBefore(published_at, 'day')) return 'ended';
  if (!now.isBefore(accessible_from, 'day')) return 'active';
  return 'scheduled';
};

/** Whole days early access opens ahead of the premiere. */
export const earlyAccessLeadDays = (
  accessibleFrom: string,
  publishedAt: string
): number =>
  dayjs(publishedAt)
    .startOf('day')
    .diff(dayjs(accessibleFrom).startOf('day'), 'day');
